import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Search, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge, CostValue, DataTable, ErrorNotice, formatMoney, Select, } from '../admin-ui';
import { useUsageInterviews } from './ai-usage-api';
import Pager from './pager';
/**
 * Interviews with their cost — ONE PAGE at a time. Search, type/status filters
 * and sorting run on the server (/admin/ai/usage/interviews/), so a customer
 * with thousands of interviews costs the same to show as one with ten. The
 * summary strip covers every interview matching the filters, not the page.
 */
const MODE_LABELS = {
    realtime: 'Realtime',
    cost_optimized: 'Cost-optimized',
    pre_interview: 'Pre-interview',
    unknown: 'Unknown',
};
export const TYPE_LABELS = {
    coding: 'Coding',
    'non-coding': 'Non-coding',
    both: 'Coding + non-coding',
};
export const STATUS = {
    completed: { label: 'Completed', tone: 'success' },
    in_progress: { label: 'In progress', tone: 'accent' },
    interrupted: { label: 'Interrupted', tone: 'warning' },
    paused: { label: 'Paused', tone: 'warning' },
    rescheduled: { label: 'Rescheduled', tone: 'neutral' },
    not_started: { label: 'Not started', tone: 'neutral' },
    cancelled: { label: 'Cancelled', tone: 'danger' },
    deleted: { label: 'Deleted', tone: 'neutral' },
};
const statusOf = (r) => { var _a; return (_a = r.status) !== null && _a !== void 0 ? _a : 'deleted'; };
export const formatDuration = (seconds) => {
    if (seconds == null)
        return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h)
        return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m)
        return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
};
/** "aditya sharma" → "Aditya Sharma". Display only; the stored name is untouched. */
export const toTitleCase = (name) => name
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
const when = (r) => { var _a, _b; return (_b = (_a = r.started_at) !== null && _a !== void 0 ? _a : r.scheduled_at) !== null && _b !== void 0 ? _b : r.last_seen; };
const SORT_FIELDS = [
    { value: 'date', label: 'Date', asc: 'Oldest first', desc: 'Newest first' },
    { value: 'cost', label: 'Cost', asc: 'Lowest first', desc: 'Highest first' },
    { value: 'duration', label: 'Duration', asc: 'Shortest first', desc: 'Longest first' },
];
const PAGE = 50;
const ALL = '';
const InterviewCostTable = ({ scope, query: externalQuery, onOpen, showSummary = true }) => {
    var _a, _b, _c, _d, _e;
    const [ownQuery, setOwnQuery] = useState('');
    const query = externalQuery !== null && externalQuery !== void 0 ? externalQuery : ownQuery;
    const [q, setQ] = useState(query.trim());
    const [type, setType] = useState(ALL);
    const [status, setStatus] = useState(ALL);
    const [sort, setSort] = useState('date');
    const [dir, setDir] = useState('desc');
    const [offset, setOffset] = useState(0);
    // Debounced: one request after typing stops, not one per keystroke.
    useEffect(() => {
        const t = window.setTimeout(() => setQ(query.trim()), 300);
        return () => window.clearTimeout(t);
    }, [query]);
    const scopeKey = JSON.stringify(scope);
    useEffect(() => setOffset(0), [scopeKey, q, type, status, sort, dir]);
    const { data, isLoading, isFetching, error, refetch } = useUsageInterviews(Object.assign(Object.assign({}, scope), { q: q || undefined, type: type || undefined, status: status || undefined, sort,
        dir, limit: PAGE, offset }));
    const rows = (_a = data === null || data === void 0 ? void 0 : data.rows) !== null && _a !== void 0 ? _a : [];
    const total = (_b = data === null || data === void 0 ? void 0 : data.total) !== null && _b !== void 0 ? _b : 0;
    const unfiltered = Object.values((_c = data === null || data === void 0 ? void 0 : data.options.statuses) !== null && _c !== void 0 ? _c : {}).reduce((a, b) => a + b, 0);
    const summary = data === null || data === void 0 ? void 0 : data.summary;
    const typeOptions = Object.entries((_d = data === null || data === void 0 ? void 0 : data.options.types) !== null && _d !== void 0 ? _d : {}).map(([value, n]) => {
        var _a;
        return ({
            value,
            label: `${(_a = TYPE_LABELS[value]) !== null && _a !== void 0 ? _a : value} (${n})`,
        });
    });
    const statusOptions = Object.keys(STATUS)
        .filter((k) => data === null || data === void 0 ? void 0 : data.options.statuses[k])
        .map((k) => ({ value: k, label: `${STATUS[k].label} (${data === null || data === void 0 ? void 0 : data.options.statuses[k]})` }));
    const narrowed = Boolean(q || type || status);
    const clear = () => {
        if (externalQuery === undefined)
            setOwnQuery('');
        setType(ALL);
        setStatus(ALL);
    };
    const columns = [
        {
            key: 'candidate',
            header: 'Candidate',
            render: (r) => (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [r.candidate_name ? (_jsx("span", { className: 'truncate font-medium text-dash-text', children: toTitleCase(r.candidate_name) })) : (_jsx("span", { className: 'truncate italic text-dash-muted', children: "Interview deleted" })), _jsxs("span", { className: 'truncate text-[11px] text-dash-muted', children: [r.interview_slug, when(r) ? ` · ${new Date(when(r)).toLocaleString()}` : ''] })] })),
        },
        {
            key: 'job',
            header: 'Job · type',
            render: (r) => {
                var _a;
                return (_jsxs("span", { className: 'flex min-w-0 flex-col gap-0.5', children: [_jsx("span", { className: 'truncate text-dash-text2', children: (_a = r.job_title) !== null && _a !== void 0 ? _a : '—' }), r.interview_type && (_jsx("span", { className: 'text-[11px] text-dash-muted', children: TYPE_LABELS[r.interview_type] }))] }));
            },
        },
        {
            key: 'mode',
            header: 'Mode',
            hideOnMobile: true,
            render: (r) => {
                var _a;
                return (_jsx("span", { className: 'whitespace-nowrap', children: _jsx(Badge, { tone: r.interview_mode === 'realtime' ? 'purple' : 'cyan', children: (_a = MODE_LABELS[r.interview_mode]) !== null && _a !== void 0 ? _a : r.interview_mode }) }));
            },
        },
        {
            key: 'duration',
            header: 'Duration',
            align: 'right',
            render: (r) => (_jsxs("span", { className: 'flex flex-col items-end', children: [_jsx("span", { className: 'tabular-nums text-dash-text', children: formatDuration(r.duration_seconds) }), r.scheduled_minutes != null && (_jsxs("span", { className: 'text-[11px] text-dash-muted', children: ["of ", r.scheduled_minutes, "m"] }))] })),
        },
        {
            key: 'status',
            header: 'Status',
            render: (r) => {
                const s = STATUS[statusOf(r)];
                return (_jsx("span", { className: 'whitespace-nowrap', children: _jsx(Badge, { tone: s.tone, title: r.end_reason ? `Ended: ${r.end_reason}` : undefined, children: s.label }) }));
            },
        },
        {
            key: 'recruiter',
            header: 'Recruiter',
            hideOnMobile: true,
            render: (r) => {
                var _a, _b;
                return (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [_jsx("span", { className: 'truncate text-dash-text2', children: (_a = r.recruiter_name) !== null && _a !== void 0 ? _a : '—' }), _jsx("span", { className: 'truncate text-[11px] text-dash-muted', children: (_b = r.organization_name) !== null && _b !== void 0 ? _b : '' })] }));
            },
        },
        {
            key: 'calls',
            header: 'Calls',
            align: 'right',
            hideOnMobile: true,
            render: (r) => _jsx("span", { className: 'tabular-nums text-dash-text2', children: r.operations }),
        },
        {
            key: 'cost',
            header: 'Cost',
            align: 'right',
            render: (r) => _jsx(CostValue, { amount: r.cost_usd, unpricedCount: r.unpriced_records }),
        },
    ];
    const field = (_e = SORT_FIELDS.find((f) => f.value === sort)) !== null && _e !== void 0 ? _e : SORT_FIELDS[0];
    const nextDir = dir === 'desc' ? 'asc' : 'desc';
    const DirIcon = dir === 'desc' ? ArrowDownWideNarrow : ArrowUpNarrowWide;
    return (_jsxs("div", { className: 'space-y-4', children: [showSummary && (_jsxs("div", { className: 'grid grid-cols-2 gap-4 rounded-xl border border-dash-border bg-dash-card p-4 aiMd:grid-cols-4', children: [[
                        ['Interviews', summary ? summary.interviews.toLocaleString() : '—'],
                        ['Spend in interviews', summary ? formatMoney(Number(summary.spend_usd)) : '—'],
                        [
                            'Avg per interview',
                            (summary === null || summary === void 0 ? void 0 : summary.avg_per_interview) != null
                                ? formatMoney(Number(summary.avg_per_interview))
                                : '—',
                        ],
                        [
                            'Avg duration',
                            (summary === null || summary === void 0 ? void 0 : summary.avg_duration_seconds) != null
                                ? `${formatDuration(summary.avg_duration_seconds)} · ${summary.timed_interviews} timed`
                                : '—',
                        ],
                    ].map(([label, value]) => (_jsxs("div", { className: 'min-w-0', children: [_jsx("div", { className: 'text-[11.5px] text-dash-muted', children: label }), isLoading ? (_jsx("div", { className: 'mt-0.5 flex h-7 items-center', children: _jsx(Skeleton, { className: 'h-5 w-20 rounded' }) })) : (_jsx("div", { className: 'mt-0.5 truncate text-[18px] font-semibold tabular-nums text-dash-text', children: value }))] }, label))), _jsx("p", { className: 'col-span-2 border-t border-dash-border pt-2.5 text-[11.5px] text-dash-muted aiMd:col-span-4', children: "Spend recorded against these interviews. Job setup \u2014 description and question generation \u2014 belongs to the job, not one interview: see Spend breakdown \u2192 Stage." })] })), _jsxs("section", { className: 'rounded-xl border border-dash-border bg-dash-card', children: [_jsxs("div", { className: 'flex flex-wrap items-end gap-2 border-b border-dash-border p-3', children: [externalQuery === undefined && (_jsxs("label", { className: 'relative min-w-[220px] flex-1', children: [_jsx("span", { className: 'sr-only', children: "Search interviews" }), _jsx(Search, { className: 'pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-dash-muted' }), _jsx("input", { type: 'search', value: ownQuery, onChange: (e) => setOwnQuery(e.target.value), placeholder: 'Search candidate, email, interview, job, recruiter\u2026', className: 'h-9 w-full rounded-lg border border-dash-border bg-dash-surface pl-8 pr-8 text-[12.5px] text-dash-text outline-none transition-colors placeholder:text-dash-muted focus:border-dash-accent max-aiMd:h-11' }), ownQuery && (_jsx("button", { type: 'button', onClick: () => setOwnQuery(''), "aria-label": 'Clear search', className: 'absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-dash-muted hover:bg-dash-hover hover:text-dash-text cursor-pointer', children: _jsx(X, { className: 'h-3.5 w-3.5' }) }))] })), _jsx(Select, { label: 'Type', value: type, onChange: setType, options: [{ value: ALL, label: 'All types' }, ...typeOptions] }), _jsx(Select, { label: 'Status', value: status, onChange: setStatus, options: [{ value: ALL, label: 'All statuses' }, ...statusOptions] }), _jsxs("div", { className: 'flex items-end gap-1', children: [_jsx(Select, { label: 'Sort by', value: sort, onChange: (v) => setSort(v), options: SORT_FIELDS.map(({ value, label }) => ({ value, label })) }), _jsxs("button", { type: 'button', onClick: () => setDir(nextDir), "aria-label": `${field[dir]} — switch to ${field[nextDir].toLowerCase()}`, title: `${field[dir]} (click for ${field[nextDir].toLowerCase()})`, className: 'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-dash-border bg-dash-surface px-2.5 text-[12px] text-dash-text2 transition-colors hover:border-dash-borderStrong hover:text-dash-text cursor-pointer max-aiMd:h-11', children: [_jsx(DirIcon, { className: 'h-4 w-4' }), field[dir]] })] }), _jsxs("div", { className: 'ml-auto flex items-center gap-2 self-center text-[11.5px] text-dash-muted', children: [_jsx("span", { className: 'tabular-nums', children: isLoading ? (_jsx(Skeleton, { className: 'inline-block h-3 w-24 rounded align-middle' })) : isFetching ? ('Updating…') : !data ? null : narrowed ? (`${total.toLocaleString()} of ${unfiltered.toLocaleString()} interviews`) : (`${total.toLocaleString()} interviews`) }), narrowed && (_jsx("button", { type: 'button', onClick: clear, className: 'font-medium text-dash-accentInk hover:underline cursor-pointer', children: "Clear" }))] })] }), _jsx("div", { className: 'p-3', children: !data && !isLoading && error ? (_jsx(ErrorNotice, { title: 'Could not load interviews.', error: error, onRetry: () => refetch() })) : (_jsx(DataTable, { columns: columns, rows: rows, rowKey: (r) => r.interview_slug, onRowClick: (r) => onOpen(r.interview_slug), loading: isLoading, loadingLabel: 'Loading interviews', skeletonRows: 8, empty: narrowed ? 'No interviews match these filters.' : 'No interview usage measured yet.' })) }), _jsx(Pager, { offset: offset, pageSize: PAGE, total: total, onChange: setOffset })] })] }));
};
export default InterviewCostTable;
